export type ConfigSyncArea =
  | 'Workspaces'
  | 'ProjectTypes'
  | 'ListViews'
  | 'Sections'
  | 'Prefixes'
  | 'HealthConfigs'
  | 'Templates'
  | 'QuotaQuestions'
  | 'Bidding';

export type ConfigSyncAction = 'Add' | 'Update' | 'Remove' | 'Unchanged';

export interface ConfigSyncAreaOption {
  id: ConfigSyncArea;
  label: string;
  description: string;
}

/** Core areas are ticked by default; secondary areas are opt-in. Order matches the backend dependency order. */
export const CONFIG_SYNC_CORE_AREAS: ConfigSyncAreaOption[] = [
  { id: 'Workspaces', label: 'Workspaces', description: 'Workspace names, styling, order and user-type access' },
  { id: 'ProjectTypes', label: 'Project types & stages', description: 'Project types, their stages, tasks and homepage sections' },
  { id: 'ListViews', label: 'Project list views', description: 'Saved list views, columns and filters per project type' },
  { id: 'Sections', label: 'Navigation sections', description: 'Site sections and their placement inside workspaces' },
  { id: 'QuotaQuestions', label: 'Quota questions', description: 'Instance quota questions, answer options and country setup; questions in use get a new version' },
  { id: 'Bidding', label: 'Bidding settings & services', description: 'Rate cards, tiers, add-ons, catalog services and per-project-type service templates' }
];

export const CONFIG_SYNC_SECONDARY_AREAS: ConfigSyncAreaOption[] = [
  { id: 'Prefixes', label: 'Project prefixes', description: 'Adds missing prefixes only; target counters are never changed' },
  { id: 'HealthConfigs', label: 'Health configs', description: 'Project health configurations' },
  { id: 'Templates', label: 'Milestone & schedule templates', description: 'Milestone templates and per-project-type schedule templates' }
];

export interface ConfigSyncRequest {
  sourceInstanceId: string;
  areas: ConfigSyncArea[];
  removeExtras: boolean;
}

export interface ConfigSyncChange {
  area: ConfigSyncArea;
  action: ConfigSyncAction;
  key: string;
  changedFields: string[];
}

export interface ConfigSyncPlan {
  sourceInstanceId: string;
  sourceInstanceName: string;
  targetInstanceId: string;
  targetInstanceName: string;
  areas: ConfigSyncArea[];
  removeExtras: boolean;
  changes: ConfigSyncChange[];
  errors: string[];
  warnings: string[];
  addCount: number;
  updateCount: number;
  removeCount: number;
  unchangedCount: number;
}

export interface ConfigSyncResult {
  sourceInstanceId: string;
  targetInstanceId: string;
  areas: ConfigSyncArea[];
  added: number;
  updated: number;
  removed: number;
  unchanged: number;
  warnings: string[];
  appliedOn: string;
  audited: boolean;
}

export interface ConfigSyncTarget {
  id: string;
  name: string;
}

/** Error body returned by the config sync endpoints when a guard fails or the plan has errors. */
export interface ConfigSyncErrorResponse {
  message: string;
  errors: string[];
}

/** Body for POST api/instance. `sourceInstanceId` wins over the legacy `template` selector. */
export interface InstanceCreateRequest {
  name: string;
  template?: string;
  sourceInstanceId?: string;
}
